import { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox } from "antd";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

// ---- Static field config (avoid recreating on each render) ----
const BASE_FIELDS = [
  {
    name: "pan_file_id",
    label: "Upload PAN Card",
    type: "file",
    fileLabel: "Upload PAN Card",
    foldername: "kycDocs",
    accept: ".png, .jpg, .jpeg",
    required: true,
  },
  {
    name: "aadhar_front_file_id",
    label: "Upload Aadhar Card (Front)",
    type: "file",
    fileLabel: "Upload Aadhar Card",
    foldername: "kycDocs",
    accept: ".png, .jpg, .jpeg",
    required: true,
  },
  {
    name: "aadhar_back_file_id",
    label: "Upload Aadhar Card (Back)",
    type: "file",
    fileLabel: "Upload Aadhar Card",
    foldername: "kycDocs",
    accept: ".png, .jpg, .jpeg",
    required: true,
  },
  {
    name: "photo_file_id",
    label: "Photo",
    type: "file",
    fileLabel: "Upload Photo",
    foldername: "kycDocs",
    accept: ".png, .jpg, .jpeg",
    required: true,
  },
  {
    name: "bank_cheque_file_id",
    label: "Bank Cheque Upload",
    type: "file",
    fileLabel: "Upload Bank Cheque",
    foldername: "kycDocs",
    accept: ".png, .jpg, .jpeg",
    required: true,
  },
];

const TOAST_CONFIG = {
  autoClose: 3000,
  closeButton: false,
  hideProgressBar: true,
};

const DocsUpload = (props) => {
  const { apis, myinfo, onHandleStep, setGoSteps } = props || {};
  const user = props?.$store?.user || {};
  const isAadharVerified = !!user?.is_aadhar_verified;

  const hasEditData =
    props?.$store?.user?.connector_documents?.length > 0 ? true : false;

  const [acknowledged, setAcknowledged] = useState(false);
  const isSubmitDisabled = !acknowledged; // Only enable after checkbox

  const [fields, setFields] = useState(BASE_FIELDS);

  // Merge server values into field config
  const mergeFieldValues = useCallback((fieldsArr, data) => {
    console.log({ fieldsArr, data });

    return fieldsArr.map((field) => {
      const value = data?.[field.name] ?? "";
      // Most custom file inputs use value/defaultValue for display
      return {
        ...field,
        defaultValue: value,
        value,
      };
    });
  }, []);

  // Pre-fill fields if edit data exists
  useEffect(() => {
    console.log({ hasEditData });

    if (!hasEditData) return;

    let docsDetails = props?.$store?.user?.connector_documents;
    console.log({ docsDetails });

    if (docsDetails?.length > 0) {
      let {
        pan_file_id,
        aadhar_front_file_id,
        aadhar_back_file_id,
        photo_file_id,
        bank_cheque_file_id,
      } = docsDetails[0] || {};

      const alteredData = {
        pan_file_id,
        aadhar_front_file_id,
        aadhar_back_file_id,
        photo_file_id,
        bank_cheque_file_id,
      };
      setFields((prev) => mergeFieldValues(prev, alteredData));
    }
  }, [hasEditData, myinfo, mergeFieldValues]);

  const requiredFieldNames = useMemo(
    () => BASE_FIELDS.filter((f) => f.required).map((f) => f.name),
    []
  );

  const allRequiredPresent = useCallback(
    (payload) => {
      // every required key must be present and non-empty
      return requiredFieldNames.every(
        (key) => payload[key] !== undefined && payload[key] !== ""
      );
    },
    [requiredFieldNames]
  );

  const handleAcknowledge = useCallback((e) => {
    setAcknowledged(!!e?.target?.checked);
  }, []);

  // Helper to safely strip a key from an object (instead of Object.except)
  const omit = (obj = {}, keys = []) =>
    Object.fromEntries(Object.entries(obj).filter(([k]) => !keys.includes(k)));

  // AppForm will call onSubmit with (event, data)
  const handleSubmit = useCallback(
    async (e, data) => {
      // prevent default if AppForm doesn't already
      e?.preventDefault?.();
      e?.stopPropagation?.();

      // Strip non-payload fields (e.g., acknowledgement) if AppForm adds them
      const payload = omit(data, ["acknowledgement"]);

      const docsObjKeys = [
        "pan_file_id",
        "aadhar_front_file_id",
        "aadhar_back_file_id",
        "photo_file_id",
        "bank_cheque_file_id",
      ];

      if (!allRequiredPresent(payload)) {
        toast.error("Required fields cannot be empty", TOAST_CONFIG);
        return;
      }

      try {
        const res = await apis?.apiUpdateConnectorDocs?.(payload);
        if (!res) {
          toast.error(
            "Failed to upload documents. Please try again.",
            TOAST_CONFIG
          );
          return;
        }

        let fObjs = Object.only(res, docsObjKeys);
        const allValid = Object.values(fObjs).every(
          (value) => value !== null && value !== undefined && value !== ""
        );

        console.log({ allValid });

        await Swal.fire({
          title: "Documents updated successfully",
          icon: "success",
          iconColor: "#234C9B",
          timer: 4000,
          showConfirmButton: false,
        });

        // Update local store with a consistent API
        if (props.store) {
          props.store("user", {
            ...user,
            connector_documents: [fObjs],
            isDocsVerified: allValid,
          });
        } else if (props.setstore) {
          props.store("user", {
            ...user,
            connector_documents: [fObjs],
            isDocsVerified: allValid,
          });
        }

        // Trigger e-sign only after a successful upload
        if (isAadharVerified) {
          const signRes = await apis?.apiSendAgreementToConnectorForEsign?.();
          if (signRes?.presigned_agreement_link) {
            props.setstore("esignlink", signRes.presigned_agreement_link);
          }
          // Move to E-sign Screen
          setGoSteps?.(5);
        } else {
          toast.error("Please verify Aadhar Number for E-sign Agreement", {
            autoClose: 10000,
            closeButton: false,
            hideProgressBar: true,
          });
          setGoSteps?.(4);
        }
      } catch (err) {
        console.error("Docs upload error:", err);
        toast.error("Something went wrong. Please try again.", TOAST_CONFIG);
      }
    },
    [apis, allRequiredPresent, isAadharVerified, props, setGoSteps, user]
  );

  return (
    <>
      <AppForm className="container" onSubmit={handleSubmit}>
        <div className="row">
          {fields.map((field, i) => (
            <div className="col-12 col-md-12 col-lg-6" key={field.name || i}>
              <AppInput {...field} />
            </div>
          ))}
        </div>

        <div className="mt-3">
          <Checkbox onChange={handleAcknowledge} checked={acknowledged}>
            By clicking on submit you have read and agreed to the
            <a
              href="#"
              className="text-primary fw-bold text-decoration-underline ms-2"
              data-bs-toggle="modal"
              data-bs-target="#dataPrivacyModal"
            >
              Wealth Avenue T&amp;C &amp; Privacy Policy
            </a>
          </Checkbox>
        </div>

        <div className="col-12 col-md-12 mt-4 d-flex justify-content-end">
          <button
            type="button"
            className="formdata-cancel-btn btn"
            onClick={(...args) => onHandleStep?.(...args, 3)}
          >
            Back
          </button>
          <button
            type="submit"
            className="btn btn-primary ms-3"
            disabled={isSubmitDisabled}
          >
            Submit
          </button>
        </div>
      </AppForm>
    </>
  );
};

export default constore(DocsUpload);
